import useFileManagement from 'api/file-management/useFileManagement';
import { CreateWorkflowNode, Workflow, WorkflowNode, WorkflowNodeType } from 'api/model';
import useModels from 'api/prompts-models/useModels';
import usePrompts from 'api/prompts/usePrompts';
import usePythonFunctions from 'api/python-functions/usePythonFunctions';
import { Box, Typography } from '@mui/material';
import {
  NodeColumnsInfo,
  getFormSchema,
  getNodeColumnsInfo,
  getNodeColorByGroup,
  getNodeConfig,
  getNodeFormComponent,
  getUiSchema,
  hasCustomForm,
} from 'api/workflows-steps/formSchema';
import useWorkflows from 'api/workflows/useWorkflows';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import FormButtons from 'components/form/FormButtons';
import { FormBlockNode } from 'components/form/FormBlock';
import useDialog from 'hooks/useDialog';
import { useNodeValidation } from 'hooks/useNodeValidation';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { ReactFlowInstance } from 'reactflow';
import { useStore } from 'store/store.ts';
import { toast } from 'react-toastify';
import useSecrets from 'api/secrets/useSecrets';
import { isEqual } from 'lodash';
import { JSONSchema7 } from 'json-schema';
import { MenuBook } from '@mui/icons-material';
import CloseButton from 'components/button/CloseButton';
import { DocsButton, HeaderActions, HeaderBox, HeaderTitle, IconBadge } from './NodeForm.styles';

type Props = {
  nodeType: WorkflowNodeType;
  nodeId: string;
  onCancel?: () => void;
  onSubmit?: (formData: CreateWorkflowNode) => void;
  initialValues: CreateWorkflowNode | WorkflowNode;
  flowInstance: ReactFlowInstance<WorkflowNode>;
  isTooltipView?: boolean;
};

const DOCS_BASE_URL = import.meta.env.VITE_DOCS_BASE_URL as string;
const CUSTOM_FORM_ID = 'custom-form-id';

function filterSchemaFieldsForTooltip(schema: JSONSchema7) {
  const fieldsToRemove = ['name', 'description'];
  if (!schema?.properties) return schema;
  const { required, properties, title, ...rest } = schema;
  const filteredProperties = Object.fromEntries(
    Object.entries(properties).filter(([key]) => !fieldsToRemove.includes(key)),
  );
  const filteredRequired = Array.isArray(required)
    ? required.filter((field: string) => !fieldsToRemove.includes(field))
    : required;

  if (!filteredProperties.configuration) {
    filteredProperties.configuration = {
      title: '',
      type: 'object',
      properties: {},
    };
  } else {
    (filteredProperties.configuration as JSONSchema7).title = '';
  }

  return { ...rest, properties: filteredProperties, required: filteredRequired };
}

const cleanupFormSchema = (schema: JSONSchema7) => {
  const { title, description, ...rest } = schema;
  return rest;
};

const NodeForm = ({
  nodeType,
  nodeId,
  onCancel,
  onSubmit,
  initialValues,
  flowInstance,
  isTooltipView = false,
}: Props) => {
  const { projectId, workflowId, workflowRevision } = useParams();
  const { prompts } = usePrompts(projectId, true);
  const { functions } = usePythonFunctions(projectId, true);
  const { models } = useModels();
  const { workflows } = useWorkflows();
  const { secrets } = useSecrets(projectId, true);
  const { files } = useFileManagement(projectId, true);
  const readOnly = !!workflowRevision || isTooltipView;
  const [loading, setLoading] = useState(false);

  const nodes = useMemo(() => flowInstance.getNodes(), [flowInstance]);
  const edges = useMemo(() => flowInstance.getEdges(), [flowInstance]);

  const embeddedWorkflows = useStore().selectedEmbedWorkflows;
  const [showDialog, Dialog] = useDialog(ConfirmationDialog);
  const [isDirty, setIsDirty] = useState(false);
  const { nodeErrors: allNodeErrors, setNodeErrors } = useStore();
  const nodeErrors = useStore((state) => state.nodeErrors[nodeId]);
  const [formData, setFormData] = useState<CreateWorkflowNode>(initialValues as CreateWorkflowNode);

  const clearNodeErrors = useCallback(() => {
    const filteredErrors = { ...allNodeErrors };
    delete filteredErrors[nodeId];
    setNodeErrors(filteredErrors);
    nodeErrors?.forEach((error) => {
      const toastId = nodeId + error;
      if (toast.isActive(toastId)) {
        toast.dismiss(toastId);
      }
    });
  }, [allNodeErrors, nodeId, nodeErrors, setNodeErrors]);

  const handleCancel = () => {
    if (isDirty) {
      showDialog(true);
    } else {
      onCancel?.();
    }
  };

  const handleChange = useCallback(
    (formData: CreateWorkflowNode) => {
      if (!readOnly && !isDirty && !isEqual(formData, initialValues)) {
        setIsDirty(true);
      }
      setFormData(formData);
    },
    [isDirty, initialValues],
  );

  const handleSaveClose = () => {
    const form = document.getElementById(CUSTOM_FORM_ID);
    if (form) {
      const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
      setTimeout(() => form.dispatchEvent(submitEvent), 100);
    }
    setIsDirty(false);
    clearNodeErrors();
  };

  const { validate } = useNodeValidation(nodeType);

  const handleSubmit = useCallback(
    async (formData: CreateWorkflowNode) => {
      if (loading) return;
      setLoading(true);

      try {
        if (hasCustomForm(formData.type) || (await validate(formData))) {
          onSubmit?.(formData);
          clearNodeErrors();
        }
      } finally {
        setLoading(false);
      }
    },
    [onSubmit, validate, loading, hasCustomForm, clearNodeErrors],
  );

  const [nodeColumns, setNodeColumns] = useState<NodeColumnsInfo[]>([]);
  const nodeColumnsCache = useRef<Map<string, NodeColumnsInfo>>(new Map());

  useEffect(() => {
    const fetchData = async () => {
      const results = await Promise.all(
        nodes.map(async (node) => {
          const nodeKey = JSON.stringify({ id: node.id, configuration: node.data.configuration });
          if (nodeColumnsCache.current.has(nodeKey)) {
            return nodeColumnsCache.current.get(nodeKey)!;
          }

          const columns = await getNodeColumnsInfo(
            node.data,
            prompts ?? [],
            models,
            files,
            workflows as Workflow[],
            embeddedWorkflows,
            workflowId,
          );
          nodeColumnsCache.current.set(nodeKey, columns);
          return columns;
        }),
      );
      const filtered = results.filter((col) => col.outputColumns.length > 0);
      setNodeColumns(filtered);
    };
    fetchData();
  }, [nodes, prompts, models, files, workflows, embeddedWorkflows, workflowId]);

  const nodeConfig = useMemo(() => getNodeConfig(nodeType), [nodeType]);
  const nodeColor = getNodeColorByGroup(nodeConfig?.group ?? '');

  const formSchema = useMemo(() => {
    const schema =
      getFormSchema(nodeType, {
        prompts,
        promptsModels: models,
        files,
        columns: nodeColumns,
        workflows: workflows as Workflow[],
        workflowId,
        secrets,
        functions,
      }) ?? {};
    return isTooltipView ? filterSchemaFieldsForTooltip(schema) : schema;
  }, [nodeType, prompts, functions, models, files, nodeColumns, workflows, workflowId, secrets, isTooltipView]);

  const formUiSchema = useMemo(() => {
    return getUiSchema(nodeType, prompts, nodeColumns, models);
  }, [nodeType, prompts, nodeColumns, models]);

  const FormComponent = useMemo(() => getNodeFormComponent(nodeType), [nodeType]);

  const sourceIds = edges.filter((edge) => edge.source == nodeId);
  const targetIds = edges.filter((edge) => edge.target == nodeId);

  const inputs = targetIds.map(
    (edge) =>
      ({
        ...(nodes.find((node) => node.id === edge.source) ?? {}),
        columns: nodeColumns.find((col) => col.id == edge.source)?.outputColumns ?? ([] as string[]),
      }) as FormBlockNode,
  );

  const outputs = sourceIds.map(
    (edge) =>
      ({
        ...(nodes.find((node) => node.id === edge.target) ?? {}),
        columns: nodeColumns.find((col) => col.id == edge.source)?.outputColumns ?? ([] as string[]),
      }) as FormBlockNode,
  );

  return (
    <>
      <HeaderBox
        className={`relative overflow-hidden flex items-center gap-4 p-4 -mx-6 -mt-6 rounded-t-2xl ${
          isTooltipView ? '-mb-4' : 'mb-6'
        }`}
        nodeColor={nodeColor}
      >
        {nodeConfig?.icon && <IconBadge nodeColor={nodeColor}>{nodeConfig.icon}</IconBadge>}
        <HeaderTitle className="flex-1 min-w-0" noWrap>
          {formSchema.title ?? nodeConfig?.name}
        </HeaderTitle>
        <HeaderActions>
          <DocsButton
            component="a"
            href={`${DOCS_BASE_URL}/steps/${nodeConfig?.group}/${nodeType}`}
            target="_blank"
            rel="noreferrer"
            size="small"
            startIcon={<MenuBook fontSize="small" />}
          >
            Docs
          </DocsButton>
          {!isTooltipView && onCancel && <CloseButton onClose={handleCancel} />}
        </HeaderActions>
      </HeaderBox>
      <FormComponent
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        schema={cleanupFormSchema(formSchema)}
        uiSchema={formUiSchema}
        initialValues={initialValues}
        nodeId={nodeId}
        showButtons={false}
        formContext={{ nodeId: nodeId ?? '', inputs, outputs, formData, isTooltipView, prompts, secrets, files }}
        formId={CUSTOM_FORM_ID}
        disabled={readOnly}
        isTooltipView={isTooltipView}
      />
      {!isTooltipView && (
        <Box
          className="sticky bottom-0 z-10 -mx-6 mt-8 flex justify-end gap-2 px-6 py-4"
          sx={{ borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
        >
          <FormButtons
            onCancel={handleCancel}
            cancelBtnIcon={null}
            formId={CUSTOM_FORM_ID}
            disabled={readOnly}
            isLoading={loading}
          />
        </Box>
      )}
      <Dialog
        title={'Save changes?'}
        onConfirm={handleSaveClose}
        onCancel={onCancel}
        buttonsSettings={{ confirmText: 'Save', cancelText: 'Discard' }}
      >
        <Typography>Save changes before closing the form?</Typography>
      </Dialog>
    </>
  );
};

export default NodeForm;
